import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, MessageCircle, ThumbsUp, Eye, PenSquare, User, Loader2, ChevronDown, ChevronUp, MessageSquare, FileText, Sparkles, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "방금 전";
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
    return d.toLocaleDateString("ko-KR");
  } catch { return ""; }
};

const getAge = (birthYear) => birthYear ? new Date().getFullYear() - birthYear : null;

function StartChatAction({ targetUserId, targetName, token, navigate, onClose }) {
  const [starting, setStarting] = useState(false);
  const handleStart = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const res = await safeFetch(`${API_URL}/api/chat/rooms/direct`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: targetUserId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "대화방을 생성하지 못했습니다.");
      toast.success(`${targetName}님과 대화를 시작합니다`);
      if (onClose) onClose();
      navigate(`/chat/${data.id}`);
    } catch (e) {
      toast.error(e.message);
      setStarting(false);
    }
  };
  return (
    <button
      onClick={handleStart}
      disabled={starting}
      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-stone-700 hover:bg-burgundy-50 hover:text-burgundy-600 rounded-lg transition-colors text-left disabled:opacity-50"
      data-testid="start-chat-btn"
    >
      {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
      대화 시작하기
    </button>
  );
}

function CommenterActionsPopover({ children, c, token, navigate, isSelf }) {
  const [open, setOpen] = useState(false);
  if (isSelf) {
    // Self: just navigate to my profile
    return <div onClick={() => navigate("/profile")} className="cursor-pointer">{children}</div>;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="cursor-pointer">{children}</div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1.5" data-testid="commenter-actions">
        <div className="px-3 py-2 border-b border-stone-100 mb-1">
          <p className="text-xs font-semibold text-stone-800 truncate">{c.author}</p>
          <p className="text-[10px] text-stone-400">프로필을 확인하거나 대화를 시작하세요</p>
        </div>
        <button
          onClick={() => { setOpen(false); navigate(`/community/author/${c.author_id}`); }}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50 rounded-lg transition-colors text-left"
          data-testid="view-profile-btn"
        >
          <User className="w-4 h-4" />
          프로필 보기
        </button>
        <StartChatAction
          targetUserId={c.author_id}
          targetName={c.author}
          token={token}
          navigate={navigate}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function CommenterChip({ c, token, navigate, isSelf }) {
  const age = getAge(c.author_birth_year);
  return (
    <CommenterActionsPopover c={c} token={token} navigate={navigate} isSelf={isSelf}>
      <div
        className="flex items-center gap-2 p-2 pr-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors border border-stone-200"
        data-testid={`commenter-chip-${c.author_id}`}
      >
        <div className="w-9 h-9 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {c.author_profile_image ? (
            <img src={c.author_profile_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className={`w-4 h-4 ${c.author_gender === "female" ? "text-pink-400" : "text-blue-400"}`} />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-stone-800 truncate max-w-[80px]">{c.author}</span>
            <MembershipBadge membership={c.author_membership} size="sm" />
            <DocumentVerifiedBadge verified={c.author_doc_verified} size="sm" />
          </div>
          <div className="text-[10px] text-stone-400">
            {age ? `${age}세` : ""}{c.author_location ? ` · ${c.author_location.split(",")[0].trim()}` : ""}
          </div>
        </div>
      </div>
    </CommenterActionsPopover>
  );
}

function PostCard({ post, navigate, currentUserId, token }) {
  const [expanded, setExpanded] = useState(false);
  const comments = post.comments || [];
  const hasComments = comments.length > 0;
  const uniqueCommenters = Array.from(
    new Map(comments.filter(c => c.author_id).map(c => [c.author_id, c])).values()
  );

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`my-post-${post.id}`}>
      {/* Post header */}
      <button
        onClick={() => navigate(`/community/post/${post.id}`)}
        className="w-full px-5 py-4 text-left hover:bg-stone-50 transition-colors block"
      >
        <h3 className="font-bold text-stone-800 text-base mb-1.5 line-clamp-2" data-testid={`my-post-title-${post.id}`}>
          {post.title}
        </h3>
        {post.content && (
          <p className="text-sm text-stone-500 line-clamp-2 mb-2">{post.content}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-stone-400">
          <span>{formatDate(post.created_at)}</span>
          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{comments.length}</span>
          <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{post.likes || 0}</span>
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0}</span>
        </div>
      </button>

      {/* Comments toggle */}
      {hasComments && (
        <>
          <div className="border-t border-stone-100 px-5 py-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center justify-between w-full text-sm text-stone-600 hover:text-burgundy-500 transition-colors"
              data-testid={`comments-toggle-${post.id}`}
            >
              <span className="flex items-center gap-2 font-medium">
                <MessageCircle className="w-4 h-4 text-burgundy-500" />
                댓글 {comments.length}개 · 댓글 단 사람들 보기
              </span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {expanded && (
            <div className="border-t border-stone-100 bg-stone-50/50 px-5 py-4 space-y-3">
              {/* Unique commenter profiles */}
              {uniqueCommenters.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-stone-500 mb-2">댓글 작성자 · 클릭하여 대화 시작</p>
                  <div className="flex flex-wrap gap-2">
                    {uniqueCommenters.map((c) => (
                      <CommenterChip
                        key={c.author_id}
                        c={c}
                        token={token}
                        navigate={navigate}
                        isSelf={c.author_id === currentUserId}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Full comment list */}
              <div>
                <p className="text-xs font-semibold text-stone-500 mb-2">전체 댓글</p>
                <div className="space-y-2">
                  {comments.map((c) => (
                    <div key={c.id} className="flex gap-2.5 p-3 bg-white rounded-lg border border-stone-100" data-testid={`comment-${c.id}`}>
                      {c.author_id ? (
                        <CommenterActionsPopover c={c} token={token} navigate={navigate} isSelf={c.author_id === currentUserId}>
                          <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 flex items-center justify-center hover:ring-2 hover:ring-burgundy-300 transition-all">
                            {c.author_profile_image ? (
                              <img src={c.author_profile_image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <User className={`w-4 h-4 ${c.author_gender === "female" ? "text-pink-400" : "text-blue-400"}`} />
                            )}
                          </div>
                        </CommenterActionsPopover>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                          <User className="w-4 h-4 text-stone-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          {c.author_id ? (
                            <CommenterActionsPopover c={c} token={token} navigate={navigate} isSelf={c.author_id === currentUserId}>
                              <span className="text-xs font-semibold text-stone-800 hover:text-burgundy-500 transition-colors cursor-pointer">
                                {c.author}
                              </span>
                            </CommenterActionsPopover>
                          ) : (
                            <span className="text-xs font-semibold text-stone-500">{c.author}</span>
                          )}
                          <MembershipBadge membership={c.author_membership} size="sm" />
                          <DocumentVerifiedBadge verified={c.author_doc_verified} size="sm" />
                          <span className="text-[10px] text-stone-400">{formatDate(c.created_at)}</span>
                        </div>
                        <p className="text-sm text-stone-700 whitespace-pre-line break-words">{c.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MatchCard({ u, token, navigate, showReason }) {
  const age = getAge(u.birth_year);
  const [starting, setStarting] = useState(false);
  const handleChat = async (e) => {
    e.stopPropagation();
    if (starting) return;
    setStarting(true);
    try {
      const res = await safeFetch(`${API_URL}/api/chat/rooms/direct`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: u.id || u.user_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "대화방 생성 실패");
      toast.success(`${u.username}님과 대화를 시작합니다`);
      navigate(`/chat/${data.id}`);
    } catch (err) {
      toast.error(err.message);
      setStarting(false);
    }
  };
  const userId = u.id || u.user_id;
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 hover:shadow-md transition-shadow" data-testid={`match-card-${userId}`}>
      <div className="flex gap-3">
        <button
          onClick={() => navigate(`/community/author/${userId}`)}
          className="w-14 h-14 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 flex items-center justify-center hover:ring-2 hover:ring-burgundy-300 transition-all"
        >
          {u.profile_image ? (
            <img src={u.profile_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className={`w-6 h-6 ${u.gender === "female" ? "text-pink-400" : "text-blue-400"}`} />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <button
              onClick={() => navigate(`/community/author/${userId}`)}
              className="text-sm font-bold text-stone-800 hover:text-burgundy-500 transition-colors truncate"
            >
              {u.username}
            </button>
            <MembershipBadge membership={u.membership_type || u.membership} size="sm" />
            <DocumentVerifiedBadge verified={u.document_verified} size="sm" />
          </div>
          <div className="text-xs text-stone-500 mb-2">
            {age ? `${age}세` : ""}
            {u.location ? ` · ${u.location.split(",")[0].trim()}` : ""}
            {u.job ? ` · ${u.job}` : ""}
          </div>
          {showReason && u.reason && (
            <div className="text-xs text-burgundy-600 bg-burgundy-50 rounded-lg px-2.5 py-1.5 mb-2 flex items-start gap-1.5">
              <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{u.reason}</span>
            </div>
          )}
          {u.liked_posts && u.liked_posts.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {u.liked_posts.slice(0, 3).map((p) => (
                <span key={p.post_id} className="text-[10px] bg-rose-50 text-rose-600 px-2 py-0.5 rounded-full border border-rose-100">
                  ♥ {p.title?.slice(0, 20) || "글"}{p.title?.length > 20 ? "…" : ""}
                </span>
              ))}
              {u.liked_posts.length > 3 && (
                <span className="text-[10px] text-stone-400">+{u.liked_posts.length - 3}</span>
              )}
            </div>
          )}
          {u.introduction && (
            <p className="text-xs text-stone-600 line-clamp-2 mb-2">{u.introduction}</p>
          )}
          <div className="flex gap-2">
            <Button
              onClick={handleChat}
              disabled={starting}
              className="btn-primary flex-1 text-xs py-2 flex items-center justify-center gap-1.5"
              data-testid={`match-chat-${userId}`}
            >
              {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
              대화 시작
            </Button>
            <button
              onClick={() => navigate(`/community/author/${userId}`)}
              className="px-3 py-2 text-xs border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
              data-testid={`match-profile-${userId}`}
            >
              프로필
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyPostsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(["posts", "ai", "likers"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "posts"); // posts | ai | likers
  // Posts tab
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // AI tab
  const [aiMatches, setAiMatches] = useState([]);
  const [aiMessage, setAiMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoaded, setAiLoaded] = useState(false);
  // Likers tab
  const [likers, setLikers] = useState([]);
  const [likersLoading, setLikersLoading] = useState(false);
  const [likersLoaded, setLikersLoaded] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    const fetchPosts = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await safeFetch(`${API_URL}/api/posts/mine/list?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("내 글을 불러오지 못했습니다.");
        const data = await res.json();
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [authLoading, isAuthenticated, token, navigate]);

  // Lazy fetch AI matches
  useEffect(() => {
    if (activeTab !== "ai" || aiLoaded || !token) return;
    const fetchAi = async () => {
      setAiLoading(true);
      try {
        const res = await safeFetch(`${API_URL}/api/posts/mine/ai-matches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          setAiMatches(data.matches || []);
          setAiMessage(data.message || "");
        } else {
          setAiMessage(data.detail || "추천을 불러오지 못했습니다.");
        }
      } catch {
        setAiMessage("추천을 불러오지 못했습니다.");
      } finally {
        setAiLoading(false);
        setAiLoaded(true);
      }
    };
    fetchAi();
  }, [activeTab, aiLoaded, token]);

  // Lazy fetch likers
  useEffect(() => {
    if (activeTab !== "likers" || likersLoaded || !token) return;
    const fetchLikers = async () => {
      setLikersLoading(true);
      try {
        const res = await safeFetch(`${API_URL}/api/posts/mine/likers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          setLikers(data.likers || []);
        }
      } catch {} finally {
        setLikersLoading(false);
        setLikersLoaded(true);
      }
    };
    fetchLikers();
  }, [activeTab, likersLoaded, token]);

  const refreshAi = () => { setAiLoaded(false); };

  // AI 추천 친구 전용 진입(?tab=ai)이면 다른 탭 숨기고 AI만 노출
  const aiOnly = searchParams.get("tab") === "ai";
  const allTabs = [
    { id: "posts", label: "내가 쓴 글", icon: FileText, count: total },
    { id: "ai", label: "AI가 추천한 인연", icon: Sparkles, count: aiLoaded ? aiMatches.length : null },
    { id: "likers", label: "내 글을 좋아해준 회원", icon: Heart, count: likersLoaded ? likers.length : null },
  ];
  const tabs = aiOnly
    ? allTabs.filter((t) => t.id === "ai")
    : allTabs.filter((t) => t.id !== "ai");

  return (
    <div className="min-h-screen bg-stone-100 pt-20 pb-12" data-testid="my-posts-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/community")}
            className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800">나의 커뮤니티</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          {/* Left Sidebar Tabs */}
          <aside className="bg-white rounded-2xl border border-stone-200 p-2 h-fit lg:sticky lg:top-24" data-testid="my-posts-sidebar">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              {tabs.map((t) => {
                const Icon = t.icon;
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`flex-shrink-0 lg:w-full flex items-center justify-between gap-2 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-burgundy-50 text-burgundy-600" : "text-stone-600 hover:bg-stone-50"}`}
                    data-testid={`tab-${t.id}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Icon className={`w-4 h-4 ${isActive ? "text-burgundy-500" : "text-stone-400"}`} />
                      <span className="whitespace-nowrap lg:whitespace-normal text-left">{t.label}</span>
                    </span>
                    {t.count !== null && t.count !== undefined && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? "bg-burgundy-100 text-burgundy-700" : "bg-stone-100 text-stone-500"}`}>
                        {t.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            {activeTab === "posts" && (
              <PostsTabContent
                loading={loading} error={error} posts={posts}
                navigate={navigate} currentUserId={user?.id} token={token}
              />
            )}
            {activeTab === "ai" && (
              <AiMatchesTabContent
                loading={aiLoading} matches={aiMatches} message={aiMessage}
                navigate={navigate} token={token} onRefresh={refreshAi}
              />
            )}
            {activeTab === "likers" && (
              <LikersTabContent
                loading={likersLoading} likers={likers}
                navigate={navigate} token={token}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function PostsTabContent({ loading, error, posts, navigate, currentUserId, token }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-stone-800">내가 쓴 글</h2>
          <p className="text-xs text-stone-500 mt-0.5">총 {posts.length}개의 글</p>
        </div>
        <Button
          onClick={() => navigate("/community/write")}
          className="btn-primary flex items-center gap-2 px-4"
          data-testid="new-post-btn"
        >
          <PenSquare className="w-4 h-4" />
          <span className="hidden sm:inline">글쓰기</span>
        </Button>
      </div>
      {loading ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Loader2 className="w-8 h-8 text-burgundy-500 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-stone-500">불러오는 중...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center text-red-600 text-sm">{error}</div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <PenSquare className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 text-sm mb-4">아직 작성한 글이 없습니다</p>
          <Button onClick={() => navigate("/community/write")} className="btn-primary" data-testid="empty-new-post-btn">
            첫 글 쓰러가기
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} navigate={navigate} currentUserId={currentUserId} token={token} />
          ))}
        </div>
      )}
    </div>
  );
}

function AiMatchesTabContent({ loading, matches, message, navigate, token, onRefresh }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            AI가 추천한 인연
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">당신의 글을 분석해 결이 맞는 이성을 추천합니다</p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-xs text-stone-500 hover:text-burgundy-500 px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50"
          data-testid="ai-refresh-btn"
        >
          새로고침
        </button>
      </div>
      {loading ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Loader2 className="w-8 h-8 text-burgundy-500 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-stone-500">AI가 당신의 글을 분석 중입니다...</p>
        </div>
      ) : matches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Sparkles className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 text-sm mb-2">{message || "추천할 이성이 아직 없습니다"}</p>
          <Button onClick={() => navigate("/community/write")} className="btn-primary mt-4" data-testid="ai-empty-write-btn">
            글 쓰러가기
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map((u) => (
            <MatchCard key={u.id || u.user_id} u={u} token={token} navigate={navigate} showReason />
          ))}
        </div>
      )}
    </div>
  );
}

function LikersTabContent({ loading, likers, navigate, token }) {
  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
          <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
          내 글을 좋아해준 회원
        </h2>
        <p className="text-xs text-stone-500 mt-0.5">내 글에 ♥ 를 눌러준 이성 회원들입니다</p>
      </div>
      {loading ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Loader2 className="w-8 h-8 text-burgundy-500 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-stone-500">불러오는 중...</p>
        </div>
      ) : likers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Heart className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 text-sm">아직 내 글을 좋아한 이성 회원이 없어요</p>
          <p className="text-stone-400 text-xs mt-1">글을 꾸준히 올려보세요 ✨</p>
        </div>
      ) : (
        <div className="space-y-3">
          {likers.map((u) => (
            <MatchCard key={u.id} u={u} token={token} navigate={navigate} showReason={false} />
          ))}
        </div>
      )}
    </div>
  );
}
